import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { Settings, Plug, DollarSign, FileText, Receipt, Brain, Key, Search, ChevronRight, FolderKanban, LayoutDashboard, Zap, Building2, User, Mail, MessageSquare, Users } from 'lucide-react';
import { XeroIntegration } from './XeroIntegration';
import { MYOBIntegration } from './MYOBIntegration';
import { OutlookIntegration } from './OutlookIntegration';
import { KudosityIntegration } from './KudosityIntegration';
import { IntegrationComingSoon } from './IntegrationComingSoon';
import { TaxRates } from './TaxRates';
import { ChartOfAccounts } from './ChartOfAccounts';
import { Invoices } from './Invoices';
import { Bills } from './Bills';
import { LeadFormSettings } from './LeadFormSettings';
import { PayrollCalendars } from './PayrollCalendars';
import { LeaveTypes } from './LeaveTypes';
import { EarningsRates } from './EarningsRates';
import { AISettings } from './AISettings';
import { APIKeysManagement } from './APIKeysManagement';
import { MainExperienceCards } from './MainExperienceCards';
import { RightSidebarQuickActions } from './RightSidebarQuickActions';
import { TrackingCodesList } from './TrackingCodesList';
import { PaymentReleaseDashboard } from './PaymentReleaseDashboard';
import { EscrowPaymentReleases } from './EscrowPaymentReleases';
import { EscrowBankAccountSettings } from './EscrowBankAccountSettings';
import { SystemSettings } from './SystemSettings';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../i18n/hooks/useTranslation';
import { useBreadcrumbs } from '../../contexts/BreadcrumbContext';
import { useNavigation } from '../../contexts/NavigationContext';

type SettingsTab = 'general' | 'integrations' | 'financial' | 'api-keys';
type GeneralGroup = 'ai' | 'experience' | 'escrow' | null;
type GeneralSubTab = 'ai' | 'leadForm' | 'mainExperienceCards' | 'rightSidebarQuickActions' | 'escrow-releases' | 'escrow-bank-account' | GeneralGroup | null;
type FinancialTab = 'tax-rates' | 'chart-of-accounts' | 'pay-calendars' | 'leave-types' | 'earnings-rates' | 'invoices' | 'bills' | 'tracking-codes' | 'payment-releases';

export const AdminSettings: React.FC = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const { setCustomBreadcrumbs } = useBreadcrumbs();
  const { currentPath } = useNavigation();
  const isSystemAdmin = user?.role === 'System Administrator';
  
  const [activeTab, setActiveTab] = useState<SettingsTab>('general');
  const [generalGroup, setGeneralGroup] = useState<GeneralGroup>(null);
  const [generalSubTab, setGeneralSubTab] = useState<GeneralSubTab>(null);
  const [financialTab, setFinancialTab] = useState<FinancialTab>('tax-rates');
  const [integrationFilter, setIntegrationFilter] = useState('');

  // Track if this component is mounted to prevent stale updates
  const isMountedRef = useRef(true);
  
  // Drag-to-scroll refs and state
  const financialNavRef = useRef<HTMLElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const hasMovedRef = useRef(false);
  
  // Read URL parameters on mount and when URL changes
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const groupParam = urlParams.get('group') as GeneralGroup;
    const subTabParam = urlParams.get('subTab') as GeneralSubTab;
    const tabParam = urlParams.get('tab') as SettingsTab;
    const financialTabParam = urlParams.get('financialTab') as FinancialTab;
    
    console.log('[AdminSettings] Reading URL parameters - tab:', tabParam, 'group:', groupParam, 'subTab:', subTabParam, 'financialTab:', financialTabParam);
    
    // Note: We don't read 'tab' param here as it's handled by Admin component
    // We use 'settingsTab' to avoid conflicts
    // Also check if 'group' is actually a settingsTab name (integrations, financial, api-keys)
    // This handles cases where the backend generates group=integrations instead of settingsTab=integrations
    const settingsTabParam = urlParams.get('settingsTab') as SettingsTab;
    const groupAsTab = groupParam && ['integrations', 'financial', 'api-keys'].includes(groupParam) ? groupParam as SettingsTab : null;
    
    if (settingsTabParam && ['general', 'integrations', 'financial', 'api-keys'].includes(settingsTabParam)) {
      setActiveTab(settingsTabParam);
    } else if (groupAsTab) {
      // If group is actually a settingsTab name, use it and clear the group
      setActiveTab(groupAsTab);
      setGeneralGroup(null);
    } else {
      // Default to 'general' if no settingsTab specified
      setActiveTab('general');
    }
    
    // Set financial tab if present in URL
    if (financialTabParam && ['tax-rates', 'chart-of-accounts', 'pay-calendars', 'leave-types', 'earnings-rates', 'invoices', 'bills', 'tracking-codes'].includes(financialTabParam)) {
      setFinancialTab(financialTabParam);
    }
    
    // Set group if present in URL
    if (groupParam && ['ai', 'experience', 'escrow'].includes(groupParam)) {
      setGeneralGroup(groupParam);
    } else {
      // If group is not present in URL, reset it
      setGeneralGroup(null);
    }
    
    // Set subTab if present in URL
    if (subTabParam) {
      setGeneralSubTab(subTabParam);
    } else {
      // If subTab is not present in URL, reset it
      setGeneralSubTab(null);
    }
  }, [currentPath]); // Run when currentPath changes
  
  // Update URL when activeTab changes
  useEffect(() => {
    const url = new URL(window.location.href);
    url.searchParams.set('tab', 'settings'); // Admin component handles 'tab', we handle sub-params
    if (activeTab !== 'general') {
      url.searchParams.set('settingsTab', activeTab);
      // Clear group and subTab when switching away from general tab
      url.searchParams.delete('group');
      url.searchParams.delete('subTab');
    } else {
      url.searchParams.delete('settingsTab');
    }
    window.history.replaceState({}, '', url.toString());
  }, [activeTab]);
  
  // Update URL when generalGroup changes
  useEffect(() => {
    const url = new URL(window.location.href);
    if (generalGroup) {
      url.searchParams.set('group', generalGroup);
    } else {
      url.searchParams.delete('group');
    }
    window.history.replaceState({}, '', url.toString());
  }, [generalGroup]);
  
  // Update URL when generalSubTab changes
  useEffect(() => {
    const url = new URL(window.location.href);
    if (generalSubTab) {
      url.searchParams.set('subTab', generalSubTab);
    } else {
      url.searchParams.delete('subTab');
    }
    window.history.replaceState({}, '', url.toString());
  }, [generalSubTab]);
  
  // Update URL when financialTab changes
  useEffect(() => {
    if (activeTab === 'financial') {
      const url = new URL(window.location.href);
      url.searchParams.set('financialTab', financialTab);
      window.history.replaceState({}, '', url.toString());
    }
  }, [financialTab, activeTab]);
  
  // Update breadcrumbs when tab changes - use useLayoutEffect for synchronous updates
  useLayoutEffect(() => {
    isMountedRef.current = true;
    
    // Always clear first to prevent accumulation
    setCustomBreadcrumbs(null);
    
    const baseBreadcrumbs = [
      { label: t('navigation.home'), path: '/', icon: 'Home' },
      { label: t('navigation.admin'), path: '/admin?tab=settings', icon: 'Settings' },
      { label: t('admin.settings'), path: '/admin?tab=settings', icon: 'Settings' }
    ];

    if (activeTab === 'general') {
      if (generalGroup === null && generalSubTab === null) {
        setCustomBreadcrumbs([
          ...baseBreadcrumbs,
          { label: t('admin.general'), path: '/admin?tab=settings', icon: 'Settings' }
        ]);
      } else if (generalGroup !== null && generalSubTab === null) {
        const groupLabels: Record<Exclude<GeneralGroup, null>, string> = {
          'ai': t('admin.aiSettings'),
          'experience': t('admin.experience'),
          'escrow': t('admin.escrowSettings') || '3PM Escrow'
        };
        setCustomBreadcrumbs([
          ...baseBreadcrumbs,
          { label: t('admin.general'), path: '/admin?tab=settings', icon: 'Settings' },
          { label: groupLabels[generalGroup] || '', path: '/admin?tab=settings', icon: 'Settings' }
        ]);
      } else if (generalSubTab !== null && generalGroup !== null && generalSubTab !== 'ai' && generalSubTab !== 'experience' && generalSubTab !== 'mainExperienceCards' && generalSubTab !== 'rightSidebarQuickActions' && generalSubTab !== 'escrow-releases' && generalSubTab !== 'escrow-bank-account') {
        const subTabLabels: Record<'leadForm' | 'mainExperienceCards' | 'rightSidebarQuickActions' | 'escrow-releases' | 'escrow-bank-account', string> = {
          'leadForm': t('admin.leadFormSettings.title'),
          'mainExperienceCards': t('admin.mainExperienceCards'),
          'rightSidebarQuickActions': t('admin.rightSidebarQuickActions'),
          'escrow-releases': t('admin.escrowPaymentReleases'),
          'escrow-bank-account': t('admin.escrowBankAccountSettings')
        };
        const groupLabels: Record<Exclude<GeneralGroup, null>, string> = {
          'ai': t('admin.aiSettings'),
          'experience': t('admin.experience'),
          'escrow': t('admin.escrowSettings') || '3PM Escrow'
        };
        setCustomBreadcrumbs([
          ...baseBreadcrumbs,
          { label: t('admin.general'), path: '/admin?tab=settings', icon: 'Settings' },
          { label: groupLabels[generalGroup] || '', path: '/admin?tab=settings', icon: 'Settings' },
          { label: subTabLabels[generalSubTab as 'mainExperienceCards' | 'rightSidebarQuickActions' | 'escrow-releases' | 'escrow-bank-account'] || '', path: '/admin?tab=settings', icon: 'Settings' }
        ]);
      } else if (generalSubTab !== null && generalGroup === null && (generalSubTab === 'ai' || generalSubTab === 'leadForm')) {
        const directLabels: Record<'ai' | 'leadForm', string> = {
          'ai': t('admin.aiSettings'),
          'leadForm': t('admin.leadFormSettings.title')
        };
        setCustomBreadcrumbs([
          ...baseBreadcrumbs,
          { label: t('admin.general'), path: '/admin?tab=settings', icon: 'Settings' },
          { label: directLabels[generalSubTab as 'ai' | 'leadForm'] || '', path: '/admin?tab=settings', icon: 'Settings' }
        ]);
      } else if (generalSubTab !== null && generalGroup === 'escrow') {
        const escrowLabels: Record<'escrow-releases' | 'escrow-bank-account', string> = {
          'escrow-releases': t('admin.escrowPaymentReleases'),
          'escrow-bank-account': t('admin.escrowBankAccountSettings')
        };
        setCustomBreadcrumbs([
          ...baseBreadcrumbs,
          { label: t('admin.general'), path: '/admin?tab=settings', icon: 'Settings' },
          { label: t('admin.escrowSettings') || '3PM Escrow', path: '/admin?tab=settings&group=escrow', icon: 'Settings' },
          { label: escrowLabels[generalSubTab as 'escrow-releases' | 'escrow-bank-account'] || '', path: '/admin?tab=settings', icon: 'Settings' }
        ]);
      }
    } else if (activeTab === 'integrations') {
      setCustomBreadcrumbs([
        ...baseBreadcrumbs,
        { label: t('admin.integrations'), path: '/admin?tab=settings', icon: 'Plug' }
      ]);
    } else if (activeTab === 'financial') {
      setCustomBreadcrumbs([
        ...baseBreadcrumbs,
        { label: t('admin.financial'), path: '/admin?tab=settings', icon: 'DollarSign' }
      ]);
    } else if (activeTab === 'api-keys') {
      setCustomBreadcrumbs([
        ...baseBreadcrumbs,
        { label: t('admin.apiKeys'), path: '/admin?tab=settings', icon: 'Key' }
      ]);
    } else {
      setCustomBreadcrumbs(null);
    }
    
    // Cleanup: mark as unmounted
    return () => {
      isMountedRef.current = false;
    };
  }, [activeTab, generalGroup, generalSubTab, t, setCustomBreadcrumbs]);

  // Drag-to-scroll handlers for financial navigation
  const handleMouseDown = (e: React.MouseEvent<HTMLElement>) => {
    if (!financialNavRef.current) return;
    setIsDragging(true);
    hasMovedRef.current = false;
    setStartX(e.pageX - financialNavRef.current.offsetLeft);
    setScrollLeft(financialNavRef.current.scrollLeft);
    financialNavRef.current.style.cursor = 'grabbing';
    financialNavRef.current.style.userSelect = 'none';
  };

  const handleMouseLeave = () => {
    if (!financialNavRef.current) return;
    setIsDragging(false);
    hasMovedRef.current = false;
    financialNavRef.current.style.cursor = 'grab';
    financialNavRef.current.style.userSelect = '';
  };

  const handleMouseUp = (e: React.MouseEvent<HTMLElement>) => {
    if (!financialNavRef.current) return;
    setIsDragging(false);
    // Reset after a short delay to allow onClick to check it
    setTimeout(() => {
      hasMovedRef.current = false;
    }, 0);
    financialNavRef.current.style.cursor = 'grab';
    financialNavRef.current.style.userSelect = '';
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLElement>) => {
    if (!isDragging || !financialNavRef.current) return;
    const x = e.pageX - financialNavRef.current.offsetLeft;
    const walk = (x - startX) * 2; // Scroll speed multiplier
    const newScrollLeft = scrollLeft - walk;
    
    // Check if we've moved significantly (more than 3 pixels)
    if (Math.abs(walk) > 3) {
      hasMovedRef.current = true;
      e.preventDefault();
      financialNavRef.current.scrollLeft = newScrollLeft;
    }
  };

  // System Admins see System Settings instead of regular settings
  if (isSystemAdmin) {
    return <SystemSettings />;
  }

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">{t('admin.settings')}</h2>
      <p className="text-gray-600 dark:text-gray-400 mb-6">{t('admin.settingsDescription')}</p>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-600 mb-6">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('general')}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'general'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            <Settings className="w-4 h-4 inline-block mr-2 -mt-1" />
            {t('admin.general')}
          </button>
          
          <button
            onClick={() => setActiveTab('integrations')}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'integrations'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            <Plug className="w-4 h-4 inline-block mr-2 -mt-1" />
            {t('admin.integrations')}
          </button>

          <button
            onClick={() => setActiveTab('financial')}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'financial'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            <DollarSign className="w-4 h-4 inline-block mr-2 -mt-1" />
            {t('admin.financial')}
          </button>

          <button
            onClick={() => setActiveTab('api-keys')}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'api-keys'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            <Key className="w-4 h-4 inline-block mr-2 -mt-1" />
            {t('admin.apiKeys')}
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'general' && (
        <div>
          {generalGroup === null && generalSubTab === null ? (
            /* Grouped Settings Cards Grid */
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {/* AI Settings Card */}
              <div 
                onClick={() => setGeneralSubTab('ai')}
                className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 p-6 cursor-pointer hover:border-primary-500 dark:hover:border-primary-500 transition-colors"
              >
                <div>
                  <div className="flex items-start justify-between mb-4">
                    <div className="p-3 bg-accent-100 dark:bg-accent-800 rounded-lg">
                      <Brain className="w-6 h-6 text-accent-700 dark:text-accent-300" />
                    </div>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2">
                    {t('admin.aiSettings')}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    {t('admin.aiSettingsDescription')}
                  </p>
                </div>
              </div>

              {/* Lead Form Settings Card */}
              <div 
                onClick={() => setGeneralSubTab('leadForm')}
                className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 p-6 cursor-pointer hover:border-primary-500 dark:hover:border-primary-500 transition-colors"
              >
                <div>
                  <div className="flex items-start justify-between mb-4">
                    <div className="p-3 bg-accent-100 dark:bg-accent-800 rounded-lg">
                      <FileText className="w-6 h-6 text-accent-700 dark:text-accent-300" />
                    </div>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2">
                    {t('admin.leadFormSettings.title')}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    {t('admin.leadFormSettings.cardDescription')}
                  </p>
                </div>
              </div>

              {/* Experience Settings Card */}
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 p-6 cursor-pointer hover:border-primary-500 dark:hover:border-primary-500 transition-colors">
                <div 
                  onClick={() => setGeneralGroup('experience')}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="p-3 bg-accent-100 dark:bg-accent-800 rounded-lg">
                      <LayoutDashboard className="w-6 h-6 text-accent-700 dark:text-accent-300" />
                    </div>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2">
                    {t('admin.experience')}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    {t('admin.experienceSettingsDescription')}
                  </p>
                </div>
                <div className="space-y-2 mb-4">
                  <div 
                    onClick={() => {
                      setGeneralGroup('experience');
                      setGeneralSubTab('mainExperienceCards');
                    }}
                    className="flex items-center text-sm text-gray-700 dark:text-gray-300 cursor-pointer hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                  >
                    <ChevronRight className="w-4 h-4 mr-2" />
                    {t('admin.mainExperienceCards')}
                  </div>
                  <div 
                    onClick={() => {
                      setGeneralGroup('experience');
                      setGeneralSubTab('rightSidebarQuickActions');
                    }}
                    className="flex items-center text-sm text-gray-700 dark:text-gray-300 cursor-pointer hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                  >
                    <ChevronRight className="w-4 h-4 mr-2" />
                    {t('admin.rightSidebarQuickActions')}
                  </div>
                </div>
              </div>

              {/* 3PM Escrow Settings Card - Hidden */}
              {/* <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 p-6 cursor-pointer hover:border-primary-500 dark:hover:border-primary-500 transition-colors">
                <div 
                  onClick={() => setGeneralGroup('escrow')}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="p-3 bg-accent-100 dark:bg-accent-800 rounded-lg">
                      <Shield className="w-6 h-6 text-accent-700 dark:text-accent-300" />
                    </div>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2">
                    {t('admin.escrowSettings') || '3PM Escrow'}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    {t('admin.escrowSettingsDescription') || 'Manage escrow payment settings and bank account configuration'}
                  </p>
                </div>
                <div className="space-y-2 mb-4">
                  <div 
                    onClick={() => {
                      setGeneralGroup('escrow');
                      setGeneralSubTab('escrow-releases');
                    }}
                    className="flex items-center text-sm text-gray-700 dark:text-gray-300 cursor-pointer hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                  >
                    <ChevronRight className="w-4 h-4 mr-2" />
                    {t('admin.escrowPaymentReleases')}
                  </div>
                  <div 
                    onClick={() => {
                      setGeneralGroup('escrow');
                      setGeneralSubTab('escrow-bank-account');
                    }}
                    className="flex items-center text-sm text-gray-700 dark:text-gray-300 cursor-pointer hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                  >
                    <ChevronRight className="w-4 h-4 mr-2" />
                    {t('admin.escrowBankAccountSettings')}
                  </div>
                </div>
              </div> */}

            </div>
          ) : generalGroup !== null && generalSubTab === null ? (
            /* Sub-options for selected group */
            <div>
              <button
                onClick={() => {
                  setGeneralGroup(null);
                  setGeneralSubTab(null);
                }}
                className="mb-6 flex items-center text-sm text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
              >
                <ChevronRight className="w-4 h-4 mr-1 rotate-180" />
                {t('common.back')}
              </button>

              {generalGroup === 'ai' && (
                <div>
                  <AISettings />
                </div>
              )}

              {generalGroup === 'experience' && (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                  <div 
                    onClick={() => setGeneralSubTab('mainExperienceCards')}
                    className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 p-6 cursor-pointer hover:border-primary-500 dark:hover:border-primary-500 transition-colors"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="p-3 bg-accent-100 dark:bg-accent-800 rounded-lg">
                        <LayoutDashboard className="w-6 h-6 text-accent-700 dark:text-accent-300" />
                      </div>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2">
                      {t('admin.mainExperienceCards')}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                      {t('admin.mainExperienceCardsDescription')}
                    </p>
                  </div>

                  <div 
                    onClick={() => setGeneralSubTab('rightSidebarQuickActions')}
                    className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 p-6 cursor-pointer hover:border-primary-500 dark:hover:border-primary-500 transition-colors"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="p-3 bg-accent-100 dark:bg-accent-800 rounded-lg">
                        <Zap className="w-6 h-6 text-accent-700 dark:text-accent-300" />
                      </div>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2">
                      {t('admin.rightSidebarQuickActions')}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                      {t('admin.rightSidebarQuickActionsDescription')}
                    </p>
                  </div>
                </div>
              )}

              {generalGroup === 'escrow' && (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                  <div 
                    onClick={() => setGeneralSubTab('escrow-releases')}
                    className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 p-6 cursor-pointer hover:border-primary-500 dark:hover:border-primary-500 transition-colors"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="p-3 bg-accent-100 dark:bg-accent-800 rounded-lg">
                        <FileText className="w-6 h-6 text-accent-700 dark:text-accent-300" />
                      </div>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2">
                      {t('admin.escrowPaymentReleases')}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                      {t('admin.escrowPaymentReleasesDescription')}
                    </p>
                  </div>

                  <div 
                    onClick={() => setGeneralSubTab('escrow-bank-account')}
                    className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 p-6 cursor-pointer hover:border-primary-500 dark:hover:border-primary-500 transition-colors"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="p-3 bg-accent-100 dark:bg-accent-800 rounded-lg">
                        <Building2 className="w-6 h-6 text-accent-700 dark:text-accent-300" />
                      </div>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2">
                      {t('admin.escrowBankAccountSettings')}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                      {t('admin.escrowBankAccountSettingsDescription')}
                    </p>
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* Content Area with Back Button */
            <div>
              <button
                onClick={() => {
                  setGeneralSubTab(null);
                  setGeneralGroup(null);
                }}
                className="mb-6 flex items-center text-sm text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
              >
                <ChevronRight className="w-4 h-4 mr-1 rotate-180" />
                {t('common.back')}
              </button>

              {generalSubTab === 'ai' && <AISettings />}
              
              {generalSubTab === 'mainExperienceCards' && <MainExperienceCards />}
              
              {generalSubTab === 'rightSidebarQuickActions' && <RightSidebarQuickActions />}
              
              {generalSubTab === 'leadForm' && (
                <div>
                  <div className="mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t('admin.leadFormSettings.title')}</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      {t('admin.leadFormSettings.description')}
                    </p>
                  </div>
                  <LeadFormSettings />
                </div>
              )}

              {generalSubTab === 'escrow-releases' && (
                <div>
                  <div className="mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t('admin.escrowPaymentReleases')}</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      {t('admin.escrowPaymentReleasesDescription')}
                    </p>
                  </div>
                  <EscrowPaymentReleases />
                </div>
              )}

              {generalSubTab === 'escrow-bank-account' && (
                <div>
                  <div className="mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t('admin.escrowBankAccountSettings')}</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      {t('admin.escrowBankAccountSettingsDescription')}
                    </p>
                  </div>
                  <EscrowBankAccountSettings />
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {activeTab === 'integrations' && (
        <div className="space-y-6">
          {/* Filter Input */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder={t('admin.filterIntegrations')}
                value={integrationFilter}
                onChange={(e) => setIntegrationFilter(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Integration Grid - 3 per row */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 items-stretch">
            {(!integrationFilter || 'xero'.includes(integrationFilter.toLowerCase())) && <XeroIntegration />}
            {(!integrationFilter || 'myob accounting financial'.includes(integrationFilter.toLowerCase())) && <MYOBIntegration />}
            {(!integrationFilter || 'outlook email microsoft'.includes(integrationFilter.toLowerCase())) && <OutlookIntegration />}
            {(!integrationFilter || 'kudosity sms text burstsms'.includes(integrationFilter.toLowerCase())) && <KudosityIntegration />}
            {(!integrationFilter || 'quickbooks accounting financial'.includes(integrationFilter.toLowerCase())) && (
              <IntegrationComingSoon
                title={t('admin.quickbooksIntegration')}
                description={t('admin.quickbooksDescription')}
                icon={
                  <svg className="w-6 h-6 text-success-600 dark:text-success-400" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M14.5 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V6.5L14.5 2zM13 8V3.5L17.5 8H13z"/>
                  </svg>
                }
              />
            )}
            {(!integrationFilter || 'gmail email'.includes(integrationFilter.toLowerCase())) && (
              <IntegrationComingSoon
                title={t('admin.gmailIntegration')}
                description={t('admin.gmailDescription')}
                icon={<Mail className="w-8 h-8 text-error-500" />}
              />
            )}
            {(!integrationFilter || 'zoho books accounting financial'.includes(integrationFilter.toLowerCase())) && (
              <IntegrationComingSoon
                title={t('admin.zohoBooksIntegration')}
                description={t('admin.zohoBooksDescription')}
                icon={<div className="text-2xl font-bold text-accent-600 dark:text-accent-400">ZB</div>}
              />
            )}
          </div>

          {/* No Results Message */}
          {integrationFilter && (
            !(
              'xero'.includes(integrationFilter.toLowerCase()) ||
              'myob accounting financial'.includes(integrationFilter.toLowerCase()) ||
              'outlook email microsoft'.includes(integrationFilter.toLowerCase()) ||
              'quickbooks accounting financial'.includes(integrationFilter.toLowerCase()) ||
              'zoho books accounting financial'.includes(integrationFilter.toLowerCase()) ||
              'gmail email'.includes(integrationFilter.toLowerCase()) ||
              'kudosity sms text burstsms'.includes(integrationFilter.toLowerCase())
            )
          ) && (
            <div className="text-center py-12">
              <p className="text-gray-500 dark:text-gray-400">{t('admin.noIntegrationsFound', { filter: integrationFilter })}</p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'financial' && (
        <div className="space-y-6">
          {/* Financial Tabs */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-600 overflow-hidden">
            <div className="border-b border-gray-200 dark:border-gray-600">
              <nav 
                ref={financialNavRef}
                className="flex overflow-x-auto scrollbar-hide cursor-grab active:cursor-grabbing select-none" 
                aria-label="Financial Tabs"
                onMouseDown={handleMouseDown}
                onMouseLeave={handleMouseLeave}
                onMouseUp={handleMouseUp}
                onMouseMove={handleMouseMove}
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              >
                {[
                  { id: 'tax-rates', label: 'Tax Rates', icon: DollarSign },
                  { id: 'chart-of-accounts', label: 'Chart of Accounts', icon: FileText },
                  { id: 'pay-calendars', label: 'Pay Calendars', icon: Receipt },
                  { id: 'leave-types', label: 'Leave Types', icon: Receipt },
                  { id: 'earnings-rates', label: 'Earnings Rates', icon: DollarSign },
                  { id: 'invoices', label: 'Invoices', icon: Receipt },
                  { id: 'bills', label: 'Bills', icon: Receipt },
                  { id: 'tracking-codes', label: 'Tracking Codes', icon: FolderKanban },
                  { id: 'payment-releases', label: 'Payment Releases', icon: DollarSign }
                ].map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={(e) => {
                        // Prevent click if we were dragging
                        if (hasMovedRef.current) {
                          e.preventDefault();
                          e.stopPropagation();
                          return;
                        }
                        setFinancialTab(tab.id as FinancialTab);
                      }}
                      className={`flex items-center space-x-2 px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                        financialTab === tab.id
                          ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                          : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:border-gray-300 dark:hover:border-gray-500'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      <span>{tab.label}</span>
                    </button>
                  );
                })}
              </nav>
            </div>
            
            <div className="p-0">
              {financialTab === 'tax-rates' && <TaxRates />}
              {financialTab === 'chart-of-accounts' && <ChartOfAccounts />}
              {financialTab === 'pay-calendars' && <PayrollCalendars />}
              {financialTab === 'leave-types' && <LeaveTypes />}
              {financialTab === 'earnings-rates' && <EarningsRates />}
              {financialTab === 'invoices' && <Invoices />}
              {financialTab === 'bills' && <Bills />}
              {financialTab === 'tracking-codes' && <TrackingCodesList />}
              {financialTab === 'payment-releases' && <PaymentReleaseDashboard />}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'api-keys' && (
        <div className="space-y-6">
          <APIKeysManagement />
        </div>
      )}
    </div>
  );
};

